import { useState, useCallback } from 'react';
import { useKeystoneSession } from '../../auth/use-keystone-session';

import { verifyQuoteItems, VerifyQuoteItem, VerifiedQuoteItem, VerifyQuoteItemsSummary } from '@external';
import { useAppToast } from '@hooks';
import { buildVerificationItems } from './quote-verification.helpers';
import { ItemizedLineItem } from '@rta/rta-blueprint-library';

type PromotionalItem = {
	sku: string;
	hsProductId?: string | null;
	adjustmentType?: string;
	priceAdjustment?: number;
	totalAdjustment?: number;
};

type ItemizedMsrpData = {
	islands?: ItemizedLineItem[] | null;
	countertops?: ItemizedLineItem[];
	backsplashes?: ItemizedLineItem[];
	items?: ItemizedLineItem[];
	accessories?: ItemizedLineItem[];
};

export type UseQuoteVerificationReturn = {
	isVerifying: boolean;
	verifiedItems: VerifiedQuoteItem[];
	summary: VerifyQuoteItemsSummary | null;
	verifyItems: (msrpData: ItemizedMsrpData, promotionalData?: PromotionalItem[] | null) => Promise<void>;
	handlePriceSelectionChange: (index: number, selection: 'hubspot' | 'tool') => void;
	resetVerification: () => void;
	buildPriceOverrides: () => Array<{
		sku: string;
		type: string;
		selectedPrice: 'hubspot' | 'tool';
		hubspotPrice: number | null;
		toolPrice: number;
	}>;
};

export const useQuoteVerification = (): UseQuoteVerificationReturn => {
	const { getToken } = useKeystoneSession();
	const appToast = useAppToast();

	const [isVerifying, setIsVerifying] = useState(false);
	const [verifiedItems, setVerifiedItems] = useState<VerifiedQuoteItem[]>([]);
	const [summary, setSummary] = useState<VerifyQuoteItemsSummary | null>(null);

	const verifyItems = useCallback(async (
		msrpData: ItemizedMsrpData,
		promotionalData?: PromotionalItem[] | null
	): Promise<void> => {
		setIsVerifying(true);
		setVerifiedItems([]);
		setSummary(null);

		const items = buildVerificationItems(msrpData, promotionalData);

		const [result, verifyError] = await verifyQuoteItems({
			items,
			getToken
		});

		setIsVerifying(false);

		if (verifyError || !result) {
			appToast.error({ title: 'Verification Failed', description: 'Could not verify items against HubSpot. You can still proceed.' });
			setVerifiedItems(items.map((item) => ({
				...item,
				hubspotPrice: null,
				hubspotProductId: null,
				hubspotProductName: null,
				isFoundInHubspot: false,
				hasPriceMismatch: false,
				priceDifference: null
			})));
			setSummary({
				totalItems: items.length,
				foundItems: 0,
				missingItems: items.length,
				itemsWithPriceMismatch: 0,
				hasIssues: true
			});
			return;
		}

		// Default to the tool price whenever we have one to offer: promotions/discounts always,
		// and mismatched line items too — HubSpot pricing is often stale and users otherwise miss the toggle.
		const itemsWithDefaultPrice = result.verifiedItems.map((item) => ({
			...item,
			selectedPrice: (item.type === 'promotion' || item.type === 'discount')
				? 'tool' as const
				: (item.hasPriceMismatch ? 'tool' as const : undefined)
		}));
		setVerifiedItems(itemsWithDefaultPrice);
		setSummary(result.summary);
	}, [getToken, appToast]);

	const handlePriceSelectionChange = useCallback((index: number, selection: 'hubspot' | 'tool'): void => {
		setVerifiedItems((prevItems) => {
			const newItems = [...prevItems];
			newItems[index] = { ...newItems[index], selectedPrice: selection };
			return newItems;
		});
	}, []);

	const resetVerification = useCallback((): void => {
		setVerifiedItems([]);
		setSummary(null);
	}, []);

	const buildPriceOverrides = useCallback(() => {
		return verifiedItems
			.filter((item) => item.hasPriceMismatch && item.selectedPrice)
			.map((item) => ({
				sku: item.sku,
				type: item.type,
				selectedPrice: item.selectedPrice as 'hubspot' | 'tool',
				hubspotPrice: item.hubspotPrice,
				toolPrice: item.currentPrice
			}));
	}, [verifiedItems]);

	return {
		isVerifying,
		verifiedItems,
		summary,
		verifyItems,
		handlePriceSelectionChange,
		resetVerification,
		buildPriceOverrides
	};
};
